import React, { useMemo } from 'react';
import { Search, Briefcase, User, ChevronDown, Plus, Sun, Moon, Sparkles } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { UserNav } from '@/components/user-nav';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { AddTransactionSheet } from '@/components/transactions/add-transaction-sheet';
import { AddClientSheet } from '@/components/clients/add-client-sheet';
import { AddQuoteSheet } from '@/components/quotes/add-quote-sheet';
import { useActiveWorkspace } from '@/hooks/use-active-workspace';
import { cn } from '@/lib/utils';

// ─── Greeting logic ──────────────────────────────────────────────────────────

function useGreeting() {
  return useMemo(() => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) return { text: 'Good morning', Icon: Sun, accent: 'text-amber-500' };
    if (hour >= 12 && hour < 17) return { text: 'Good afternoon', Icon: Sun, accent: 'text-orange-500' };
    if (hour >= 17 && hour < 21) return { text: 'Good evening', Icon: Sun, accent: 'text-rose-500' };
    return { text: 'Working late?', Icon: Moon, accent: 'text-indigo-400' };
  }, []);
}

// Friendly short day + date: "Tue, 29 Apr"
function useTodayLabel() {
  return useMemo(() => {
    return new Date().toLocaleDateString('en-US', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
  }, []);
}

// ─── Workspace config ────────────────────────────────────────────────────────

const WORKSPACE_CONFIG = {
  personal: {
    label: 'Personal',
    Icon: User,
    bg: 'bg-violet-600',
    shadow: 'shadow-violet-200',
    ring: 'ring-violet-100',
    dot: 'bg-violet-500',
  },
  business: {
    label: 'Business',
    Icon: Briefcase,
    bg: 'bg-indigo-600',
    shadow: 'shadow-indigo-200',
    ring: 'ring-indigo-100',
    dot: 'bg-indigo-500',
  },
} as const;

// ─── Component ───────────────────────────────────────────────────────────────

export function DashboardHeader() {
  const { activeWorkspace, setActiveWorkspace } = useActiveWorkspace();
  const greeting = useGreeting();
  const todayLabel = useTodayLabel();
  const ws = WORKSPACE_CONFIG[activeWorkspace];

  return (
    <div className="sticky top-0 z-40 w-full border-b border-slate-100 bg-white/90 backdrop-blur-xl">
      <div className="container-padding h-16 sm:h-[4.5rem] flex items-center justify-between gap-3 sm:gap-5">

        {/* ── LEFT: Greeting + Workspace ──────────────────────────────── */}
        <div className="flex items-center gap-3">

          {/* Greeting block — hidden on mobile, shown sm+ */}
          <div className="hidden lg:flex flex-col leading-tight mr-1">
            <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-widest text-slate-400">
              <greeting.Icon className={cn('h-3 w-3', greeting.accent)} />
              {greeting.text}
            </span>
            <span className="text-[13px] font-bold text-slate-800">{todayLabel}</span>
          </div>

          {/* Divider — only visible when greeting is shown */}
          <div className="hidden lg:block h-8 w-px bg-slate-100 mx-1" />

          {/* Workspace Switcher */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="h-11 px-2.5 gap-2.5 rounded-2xl hover:bg-slate-50 transition-all group focus-visible:ring-2 focus-visible:ring-indigo-200"
              >
                {/* Avatar icon */}
                <div
                  className={cn(
                    'h-8 w-8 rounded-xl flex items-center justify-center text-white shadow-md transition-transform group-hover:scale-105',
                    ws.bg,
                    ws.shadow,
                  )}
                >
                  <ws.Icon className="h-4 w-4" />
                </div>

                {/* Labels */}
                <div className="text-left hidden sm:block">
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider leading-none mb-0.5">
                    Workspace
                  </p>
                  <p className="text-sm font-bold text-slate-900 flex items-center gap-1 leading-none">
                    {ws.label}
                    <ChevronDown className="h-3 w-3 text-slate-400 group-data-[state=open]:rotate-180 transition-transform" />
                  </p>
                </div>
              </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent
              align="start"
              className="w-52 p-1.5 rounded-2xl border-slate-100 shadow-xl shadow-slate-100/80"
            >
              {(Object.keys(WORKSPACE_CONFIG) as Array<keyof typeof WORKSPACE_CONFIG>).map((key) => {
                const opt = WORKSPACE_CONFIG[key];
                const isActive = activeWorkspace === key;
                return (
                  <DropdownMenuItem
                    key={key}
                    onClick={() => setActiveWorkspace(key)}
                    className={cn(
                      'rounded-xl gap-3 py-2.5 px-3 cursor-pointer transition-colors',
                      isActive && 'bg-slate-50',
                    )}
                  >
                    <div className={cn('h-7 w-7 rounded-lg flex items-center justify-center text-white shadow-sm', opt.bg)}>
                      <opt.Icon className="h-3.5 w-3.5" />
                    </div>
                    <span className="font-semibold text-sm text-slate-800">{opt.label}</span>
                    {isActive && (
                      <span className="ml-auto flex h-2 w-2 rounded-full bg-emerald-400" />
                    )}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* ── CENTER: Search ───────────────────────────────────────────── */}
        <div className="flex-1 max-w-lg relative group hidden md:block">
          <div className="absolute inset-y-0 left-3.5 flex items-center pointer-events-none">
            <Search className="h-4 w-4 text-slate-350 group-focus-within:text-indigo-500 transition-colors" />
          </div>
          <Input
            type="search"
            placeholder="Search transactions, clients, goals…"
            className="w-full pl-10 pr-20 h-10 bg-slate-50 border-slate-100 rounded-xl focus:bg-white focus:border-indigo-200 focus:ring-4 focus:ring-indigo-50 transition-all text-sm placeholder:text-slate-400"
          />
          <div className="absolute inset-y-0 right-3 flex items-center gap-1 pointer-events-none">
            <kbd className="hidden sm:inline-flex h-5 select-none items-center gap-0.5 rounded-md border border-slate-200 bg-white px-1.5 font-sans text-[10px] font-semibold text-slate-400 shadow-sm">
              <span className="text-[11px]">⌘</span>K
            </kbd>
          </div>
        </div>

        {/* ── RIGHT: Actions + User ────────────────────────────────────── */}
        <div className="flex items-center gap-2 sm:gap-3">

          {/* Context-aware quick actions */}
          {activeWorkspace === 'personal' ? (
            <AddTransactionSheet />
          ) : (
            <div className="flex items-center gap-2">
              <AddQuoteSheet />
              <AddClientSheet />
            </div>
          )}

          {/* Divider */}
          <div className="h-8 w-px bg-slate-100" />

          {/* User nav — let it render its own avatar/menu */}
          <UserNav />
        </div>

      </div>
    </div>
  );
}